import React, { useMemo, useState } from 'react';
import { Formik } from 'formik';
import { withStyles, Button, Typography, CircularProgress } from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';
import heic2any from 'heic2any';

import config from '../../../config/config';
import { snackActions } from '../../sc-utilities/SnackBar';

const styles = (theme) => ({
    root: {
        width: 'auto',
        height: 'auto',
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: '100%',
        marginTop: '10px',
        'z-index': 1000,
    },
    formControl: {
        width: '100%',
    },
    imgDiv: {
        margin: '0 auto',
        maxWidth: 600,
        maxHeight: 600,
    },
    img: {
        width: '100%',
        height: '100%',
        maxWidth: window.innerWidth - 100,
        maxHeight: window.innerHeight - 100,
    },
});

const baseStyle = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '20px',
    borderWidth: 2,
    borderRadius: 2,
    borderColor: '#eeeeee',
    borderStyle: 'dashed',
    backgroundColor: '#fafafa',
    color: '#bdbdbd',
    outline: 'none',
    transition: 'border .24s ease-in-out',
};

const focusedStyle = {
    borderColor: '#2196f3',
};

const acceptStyle = {
    borderColor: '#00e676',
};

const rejectStyle = {
    borderColor: '#ff1744',
};

const thumbsContainer = {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 16,
};

const thumb = {
    display: 'inline-flex',
    borderRadius: 2,
    border: '1px solid #eaeaea',
    marginBottom: 8,
    marginRight: 8,
    width: 100,
    height: 100,
    padding: 4,
    boxSizing: 'border-box',
};

const thumbInner = {
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden',
};

const img = {
    display: 'block',
    width: 'auto',
    height: '100%',
};

const MediaForm = (props) => {
    const {
        classes,
        formSettings,
        initialValues,
        loading,
        handleSubmit,
        translations,
        handleUpload,
        edit,
        handleRemove,
    } = props;

    const [isProcessingFiles, setIsProcessingFiles] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [files, setFiles] = useState([]);

    const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
        onDropAccepted: async (acceptedFiles) => {
            setFiles([
                ...files,
                ...(await Promise.all(
                    acceptedFiles.map(async (file) => {
                        const pos = file.name.lastIndexOf('.');
                        const extensionName = file.name.substring(pos, file.name.length).toLowerCase();
                        let preview = '';
                        if (
                            config?.upload?.media?.supportHeicPreview &&
                            ['.heic', '.heics', '.heif', '.heifs'].includes(extensionName)
                        ) {
                            const blob = new Blob([new Uint8Array(await file.arrayBuffer())]);
                            const convertedFile = await heic2any({
                                blob,
                                toType: 'image/jpeg',
                                quality: 0.1,
                            });
                            preview = URL.createObjectURL(convertedFile);
                        } else {
                            preview = URL.createObjectURL(file);
                        }
                        return Object.assign(file, {
                            preview,
                        });
                    })
                )),
            ]);
            setIsProcessingFiles(true);
            await handleUpload(acceptedFiles);
            setIsProcessingFiles(false);
        },
        maxFiles: config?.upload?.media?.maxFiles ?? 15,
        maxSize: (config?.upload?.media?.maxSizePerImage ?? 10) * 1024 * 1024, // 10 MiB / image
        onDropRejected: (fileRejections, event) => {
            for (const file of fileRejections) {
                if (file?.errors?.[0]?.code === 'too-many-files') {
                    snackActions.snackBar({
                        message: translations['maximum_x_files_are_allowed']
                            ? translations['maximum_x_files_are_allowed'].replace(
                                  '{MaxFiles}',
                                  config?.upload?.media?.maxFiles
                              )
                            : `Maximum ${config?.upload?.media?.maxFiles} files are allowed.`,
                        options: {
                            variant: 'error',
                        },
                    });
                    return;
                } else {
                    file.errors.forEach((error) => {
                        if (error.code === 'file-too-large') {
                            snackActions.snackBar({
                                message: translations['is_larger_than_the_allowed_x']
                                    ? `${file.file.name} ${translations['is_larger_than_the_allowed_x'].replace(
                                          '{MaxSize}',
                                          `${config?.upload?.media?.maxSizePerImage} MiB`
                                      )}`
                                    : `${file.file.name} is larger than the allowed ${config?.upload?.media?.maxSizePerImage} MiB.`,
                                options: {
                                    variant: 'error',
                                    autoHideDuration: 8000,
                                },
                            });
                        } else {
                            snackActions.snackBar({
                                message: `${error.message}`,
                                options: {
                                    variant: 'error',
                                    autoHideDuration: 8000,
                                },
                            });
                        }
                    });
                }
            }
        },
        accept: {
            'image/heic': [],
            'image/heic-sequence': [],
            'image/heif': [],
            'image/heif-sequence': [],
            'image/png': [],
            'image/jpeg': [],
            'image/gif': [],
            'image/webp': [],
        },
        noClick: (() => {
            if (submitting || isProcessingFiles) {
                return true;
            }

            return false;
        })(),
    });

    const style = useMemo(
        () => ({
            ...baseStyle,
            ...(isFocused ? focusedStyle : {}),
            ...(isDragAccept ? acceptStyle : {}),
            ...(isDragReject ? rejectStyle : {}),
        }),
        [isFocused, isDragAccept, isDragReject]
    );

    const thumbs = files.map((file) => (
        <div style={thumb} key={file.name}>
            <div style={thumbInner}>
                <img
                    src={file.preview}
                    style={img}
                    // Revoke data uri after image is loaded
                    onLoad={() => {
                        URL.revokeObjectURL(file.preview);
                    }}
                    alt={file.name}
                />
            </div>
        </div>
    ));

    return (
        <div className={classes.root}>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting: setFormSubmitting }) => {
                    setFormSubmitting(true);
                    setSubmitting(true);
                    await handleSubmit(values);
                    setFormSubmitting(false);
                    setSubmitting(false);
                }}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {(formikProps) => {
                    const { isValid, handleSubmit, dirty, isSubmitting } = formikProps;
                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={'h6'}>
                                {formSettings.FormTitle ? formSettings.FormTitle : null}
                                {files.length
                                    ? ` (${
                                          translations['x_added']
                                              ? translations['x_added'].replace('{NumberOfFiles}', files.length)
                                              : `${files.length} added`
                                      })`
                                    : null}
                            </Typography>
                            <section>
                                <div {...getRootProps({ style })}>
                                    <input {...getInputProps()} />
                                    <p>
                                        {translations['drag_and_drop_image_files'] ||
                                            'Drag and drop image file(s) here or click'}
                                    </p>
                                </div>
                                <aside style={thumbsContainer}>{thumbs}</aside>
                                {files.length ? (
                                    <Button
                                        variant="contained"
                                        disabled={!files.length}
                                        startIcon={<DeleteIcon />}
                                        onClick={() => {
                                            handleRemove();
                                            setFiles([]);
                                        }}
                                    >
                                        {translations['remove_files'] || 'Remove files'}
                                    </Button>
                                ) : null}
                            </section>
                            <div>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        type="submit"
                                        disabled={(() => {
                                            if (!(isValid && dirty) || isSubmitting || isProcessingFiles) {
                                                return loading || isSubmitting || isProcessingFiles;
                                            } else {
                                                if (!edit) {
                                                    return loading || isSubmitting || isProcessingFiles;
                                                }
                                            }

                                            return false;
                                        })()}
                                    >
                                        {isSubmitting || isProcessingFiles ? (
                                            <CircularProgress size={24} />
                                        ) : (
                                            formSettings.ButtonSubmit.Title
                                        )}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div>
    );
};

export default withStyles(styles, { withTheme: true })(MediaForm);
